<template>
  <Toast />
  <ConfirmDialog group="headless">
    <template #container="{ message, acceptCallback, rejectCallback }">
      <div class="flex flex-col items-center p-8 bg-surface-0 dark:bg-surface-900 rounded">
        <div
          class="rounded-full bg-white outline outline-black inline-flex justify-center items-center h-24 w-24 -mt-20">
          <i class="pi pi-question text-5xl"></i>
        </div>
        <span class="font-bold text-2xl block mb-2 mt-6">{{ message.header }}</span>
        <p class="mb-0">{{ message.message }}</p>
        <div class="flex items-center gap-2 mt-6">
          <Button :label="message.rejectLabel" outlined @click="rejectCallback" class="w-32"></Button>
          <Button :label="message.acceptLabel" @click="acceptCallback" class="w-32"></Button>
        </div>
      </div>
    </template>
  </ConfirmDialog>
  <IndexView />
  <div id="overlay" class="fixed inset-0 bg-black bg-opacity-30 z-30" :class="{ hidden: !isBlur }"></div>
  <ViewContainer>
    <section class="p-6">
      <PageTitle title="User" />
      <div class="border-t-2 border-primary bg-white dark:bg-dark-900 w-full rounded-lg py-6 px-5 shadow-lg shadow-light-100 dark:shadow-dark-500 dark:text-dark-500">
        <div id="table-header" class="mb-5 flex flex-col md:flex-row md:justify-between gap-3">
          <AddButton v-if="!isTrash" to_url="/user/tambah-user" button_text="+ Tambah User" button_class="text-base py-2 font-medium" />
          <button v-if="isTrash" @click="showTrash" class="hover:bg-hover-primary bg-primary text-white font-medium flex items-center rounded-lg px-4 py-2 cursor-pointer">
            <ArrowPathIcon class="w-5 h-5 mr-2" />
            Semua Data</button>
          <button v-if="!isTrash" @click="showTrash" class="hover:bg-hover-warning bg-warning text-primary font-medium flex items-center rounded-lg px-4 py-2 cursor-pointer">
            <TrashIcon class="w-5 h-5 mr-2" />
            Data Dihapus</button>
        </div>
        <div class="overflow-x-auto">
          <div class="inline-block min-w-full align-middle">
            <div class="overflow-hidden shadow-lg rounded-lg">
              <TableComponent>
                <TableHead>
                  <TableRow>
                    <TableHeadCell>No</TableHeadCell>
                    <TableHeadCell>Name</TableHeadCell>
                    <TableHeadCell>Email</TableHeadCell>
                    <TableHeadCell>Role</TableHeadCell>
                    <TableHeadCell>Phone Number</TableHeadCell>
                    <TableHeadCell>Action</TableHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow v-if="allUsers.length == 0">
                    <TableCell colspan="8" customClass="text-center italic text-gray-400 dark:text-gray-500"
                      noTextColor>
                      Belum ada data
                    </TableCell>
                  </TableRow>
                  <TableRow v-else v-for="(user, index) in allUsers" :key="user.id" :index="index" isBody>
                    <TableCell>{{ index + 1 }}</TableCell>
                    <TableCell>{{ user?.name }}</TableCell>
                    <TableCell>{{ user?.email }}</TableCell>
                    <TableCell>
                      <span :class="{
                        'bg-info text-white': user?.Role?.name === 'Kasir',
                        'bg-success text-white': user?.Role?.name === 'Kitchen',
                        'bg-warning text-white': user?.Role?.name === 'Admin'
                      }" class="px-2 py-1 rounded-full text-xs font-medium">
                        {{ user?.Role?.name }}
                      </span>
                    </TableCell>
                    <TableCell>{{ user?.phone }}</TableCell>
                    <TableCell noTextColor customClass="text-gray-500 dark:text-gray-400 flex">
                      <RouterLink v-if="!isTrash" :to="'/user/edit-user/' + user?.id" custom v-slot="{ navigate }">
                        <button @click="navigate" :id="'edit-data-' + user?.id"
                          class="bg-warning hover:bg-hover-warning flex justify-center items-center mx-1 p-2 rounded-md group">
                          <PencilSquareIcon class="h-4 w-4 text-light-900 group-hover:text-primary" />
                        </button>
                      </RouterLink>
                      <button v-if="!isTrash" :id="'delete-data-' + user.id"
                        class="bg-danger text-white hover:bg-hover-danger flex justify-center items-center mx-1 p-2 rounded-md group"
                        @click="deleteUser(user.id)">
                        <TrashIcon class="h-4 w-4 group-hover:text-light-50" />
                      </button>
                      <button v-if="isTrash" :id="'restore-data-' + user.id"
                        class="bg-success text-white hover:bg-hover-success flex justify-center items-center mx-1 p-2 rounded-md group"
                        @click="restoreUser(user.id)">
                        <ArrowPathIcon class="h-4 w-4 group-hover:text-light-50" />
                      </button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </TableComponent>
              <PaginationButtons v-model="currentPage" :totalPages="totalPages" />
            </div>
          </div>
        </div>
      </div>
    </section>
  </ViewContainer>
</template>

<script>
import IndexView from "@/views/IndexView.vue";
import PageTitle from "@/components/PageTitle.vue";
import AddButton from "@/components/buttons/AddButton.vue";
import ViewContainer from "@/components/ViewContainer.vue";
import {
  TableComponent,
  TableHead,
  TableRow,
  TableHeadCell,
  TableBody,
  TableCell,
} from "@/components/table";
import { PencilSquareIcon, TrashIcon, ArrowPathIcon } from "@heroicons/vue/24/solid";
import PaginationButtons from "@/components/PaginationButtons.vue";
import { mapGetters } from "vuex";
import { Button, ConfirmDialog, Toast } from "primevue";

export default {
  name: "UserView",
  components: {
    IndexView,
    PageTitle,
    AddButton,
    ViewContainer,
    TableComponent,
    TableHead,
    TableRow,
    TableHeadCell,
    TableBody,
    TableCell,
    PencilSquareIcon,
    TrashIcon,
    PaginationButtons,
    Toast,
    ConfirmDialog,
    Button,
    ArrowPathIcon
  },
  data() {
    return {
      modalShowUnit: false,
      isBlur: false,
      selectedUnit: null,
      currentPage: 1,
      totalPages: 1,
      isTrash: this.$route.query.trash,
    }
  },
  computed: {
    ...mapGetters('users', ['allUsers']),
    ...mapGetters('roles', ['allRoles']),
  },
  async created() {
    this.$store.dispatch('roles/fetchRoles')
    this.fetchUsers();
  },
  watch: {
    currentPage: {
      immediate: true,
      handler(newVal) {
        this.fetchUsers({ page: newVal });
      },
    }
  },
  methods: {
    async fetchUsers(params = {}) {
      if (!params.page) params.page = this.currentPage;
      if (!params.limit) params.limit = 50;
      if (this.isTrash) params.deletedAt = true;
      
      try {
        const { pagination } = await this.$store.dispatch('users/fetchUsers', params);
        if (pagination) {
          this.totalPages = pagination.totalPages;
        }
      } catch (error) {
        console.log(error);
      }
    },
    showModal() {
      this.modalShowUnit = true;
      this.isBlur = true;
      this.selectedUnit = null;
    },
    closeModal() {
      setTimeout(() => {
        this.modalShowUnit = false;
        this.isBlur = false;
      }, 200);
    },
    getRoles(roleId) {
      const role = this.allRoles.find(role => role.id === roleId);
      return role ? role : 'Tidak ada role';
    },
    deleteUser(id) {
      this.$confirm.require({
        group: 'headless',
        header: 'Apakah anda yakin ingin menghapus user?',
        message: 'Silahkan konfirmasi sebelum diproses.',
        acceptLabel: 'Ya',
        rejectLabel: 'Tidak',
        accept: async () => {
          try {
            await this.$store.dispatch('users/deleteUserById', id);
            this.fetchUsers();
            this.$toast.add({ severity: 'success', summary: 'Success', detail: 'Berhasil menghapus user', life: 3000 });
          }
          catch (error) {
            console.error('Gagal menghapus user', error);
            this.$toast.add({ severity: 'error', summary: 'Failed', detail: 'Gagal menghapus user', life: 3000 });
          }
        },
        reject: () => {
          this.$toast.add({ severity: 'error', summary: 'Rejected', detail: 'Batal menghapus user', life: 3000 });
        }
      });
    },
    restoreUser(id) {
      this.$confirm.require({
        group: 'headless',
        header: 'Apakah anda yakin ingin memulihkan user?',
        message: 'Silahkan konfirmasi sebelum diproses.',
        acceptLabel: 'Ya',
        rejectLabel: 'Tidak',
        accept: async () => {
          try {
            await this.$store.dispatch('users/restoreUserById', id);
            this.fetchUsers();
            this.$toast.add({ severity: 'success', summary: 'Success', detail: 'Berhasil memulihkan user', life: 3000 });
          }
          catch (error) {
            console.error('Gagal memulihkan user', error);
            this.$toast.add({ severity: 'error', summary: 'Failed', detail: 'Gagal memulihkan user', life: 3000 });
          }
        },
        reject: () => {
          this.$toast.add({ severity: 'error', summary: 'Rejected', detail: 'Batal memulihkan user', life: 3000 });
        }
      });
    },
    showTrash() {
      this.isTrash = !this.isTrash;

      if (this.isTrash) {
        this.$router.push({ query: { ...this.$route.query, trash: this.isTrash } });
      } else {
        this.$router.push('/user');
      }
      this.fetchUsers();
    }
  }
}
</script>